import React from 'react';

const departmentTemplates = {
  "U-PT-001": { // Product & Technology
    defaultRole: "R-PT-001",
    defaultGrade: "G4",
    defaultLevel: "Mid-Senior",
    defaultLocation: "Tech Hub - Building 2",
    onboardingFlow: "tech-onboarding",
    description: "Standard onboarding for technology roles with access to development tools and environments.",
    requiredSkills: ["Programming", "Problem Solving", "Collaboration"],
    equipment: ["Laptop", "Dual Monitors", "Headphones"],
    softwareAccess: ["GitHub", "Jira", "AWS Console"],
    onboardingDuration: "2 weeks",
    color: "#2563eb",
    icon: "code"
  },
  "U-MG-002": { // Marketing & Growth
    defaultRole: "R-MG-001",
    defaultGrade: "G3",
    defaultLevel: "Mid-Senior",
    defaultLocation: "Marketing Wing - Building 1",
    onboardingFlow: "marketing-onboarding",
    description: "Marketing staff onboarding with access to creative tools and marketing platforms.",
    requiredSkills: ["Content Creation", "Market Analysis", "Campaign Management"],
    equipment: ["Laptop", "Graphics Tablet", "High-Res Display"],
    softwareAccess: ["Adobe Creative Suite", "HubSpot", "Mailchimp"],
    onboardingDuration: "1.5 weeks",
    color: "#d946ef",
    icon: "campaign"
  },
  "U-SD-003": { // Sales & Business Dev
    defaultRole: "R-SD-001",
    defaultGrade: "G3",
    defaultLevel: "Mid-Senior",
    defaultLocation: "Sales Office - Downtown",
    onboardingFlow: "sales-onboarding",
    description: "Sales onboarding with CRM access and sales enablement tools.",
    requiredSkills: ["Negotiation", "Client Communication", "Market Knowledge"],
    equipment: ["Laptop", "Mobile Phone", "Sales Tablet"],
    softwareAccess: ["Salesforce", "Outreach", "Zoom Enterprise"],
    onboardingDuration: "2 weeks",
    color: "#ea580c",
    icon: "trending_up"
  },
  "U-CS-004": { // Customer Success
    defaultRole: "R-CS-001",
    defaultGrade: "G2",
    defaultLevel: "Junior",
    defaultLocation: "Customer Center - Main Building",
    onboardingFlow: "customer-service-onboarding",
    description: "Customer success team onboarding with helpdesk and support tools.",
    requiredSkills: ["Customer Service", "Problem Resolution", "Product Knowledge"],
    equipment: ["Laptop", "Headset", "Dual Monitors"],
    softwareAccess: ["Zendesk", "Intercom", "Internal Knowledge Base"],
    onboardingDuration: "1 week",
    color: "#16a34a",
    icon: "support_agent"
  },
  "U-OP-005": { // Operations
    defaultRole: "R-OP-001",
    defaultGrade: "G3",
    defaultLevel: "Mid-Senior",
    defaultLocation: "Operations Center - Building 3",
    onboardingFlow: "operations-onboarding",
    description: "Operations staff onboarding with logistics and project management tool access.",
    requiredSkills: ["Process Optimization", "Project Management", "Data Analysis"],
    equipment: ["Laptop", "Dual Monitors", "Mobile Device"],
    softwareAccess: ["SAP", "Asana", "Power BI"],
    onboardingDuration: "2 weeks",
    color: "#ca8a04",
    icon: "settings"
  },
  "U-DA-006": { // Data & Analytics
    defaultRole: "R-DA-001",
    defaultGrade: "G4",
    defaultLevel: "Mid-Senior",
    defaultLocation: "Data Center - Tech Wing",
    onboardingFlow: "data-onboarding",
    description: "Data team onboarding with analytics platforms and database access.",
    requiredSkills: ["Data Analysis", "SQL", "Statistical Modeling"],
    equipment: ["High-Performance Workstation", "Dual 4K Monitors", "Noise-Cancelling Headphones"],
    softwareAccess: ["Tableau", "SQL Server", "Python/R Environment"],
    onboardingDuration: "2 weeks",
    color: "#0891b2",
    icon: "analytics"
  },
  "U-LC-007": { // Legal & Compliance
    defaultRole: "R-LC-001",
    defaultGrade: "G5",
    defaultLevel: "Senior",
    defaultLocation: "Legal Office - Building 1",
    onboardingFlow: "legal-onboarding",
    description: "Legal team onboarding with access to compliance systems and legal document management.",
    requiredSkills: ["Legal Analysis", "Contract Review", "Regulatory Compliance"],
    equipment: ["Laptop", "Document Scanner", "Secure Storage"],
    softwareAccess: ["LexisNexis", "DocuSign", "Case Management System"],
    onboardingDuration: "3 weeks",
    color: "#4f46e5",
    icon: "gavel"
  },
  "U-FN-008": { // Finance
    defaultRole: "R-FN-001",
    defaultGrade: "G4",
    defaultLevel: "Senior",
    defaultLocation: "Finance Wing - Main Building",
    onboardingFlow: "finance-onboarding",
    description: "Finance team onboarding with access to financial systems and reporting tools.",
    requiredSkills: ["Financial Analysis", "Budgeting", "Reporting"],
    equipment: ["Laptop", "Calculator", "Dual Monitors"],
    softwareAccess: ["SAP Finance", "QuickBooks", "Excel Advanced"],
    onboardingDuration: "2 weeks",
    color: "#059669",
    icon: "account_balance"
  },
  "U-HR-009": { // Human Resources
    defaultRole: "R-HR-001",
    defaultGrade: "G3",
    defaultLevel: "Mid-Senior",
    defaultLocation: "HR Office - Main Building",
    onboardingFlow: "hr-onboarding",
    description: "HR team onboarding with access to HR information systems and employee management tools.",
    requiredSkills: ["Talent Management", "Employee Relations", "HR Policies"],
    equipment: ["Laptop", "Document Scanner", "Mobile Phone"],
    softwareAccess: ["Workday", "ATS System", "Benefits Portal"],
    onboardingDuration: "1.5 weeks",
    color: "#dc2626",
    icon: "people"
  },
};

// Also add the getDepartmentIcon helper function since it's closely related
const getDepartmentIcon = (deptId, departmentTemplates, BusinessIcon) => {
  const iconMap = {
    "code": <span className="material-icons">code</span>,
    "campaign": <span className="material-icons">campaign</span>,
    "trending_up": <span className="material-icons">trending_up</span>,
    "support_agent": <span className="material-icons">support_agent</span>,
    "settings": <span className="material-icons">settings</span>,
    "analytics": <span className="material-icons">analytics</span>,
    "gavel": <span className="material-icons">gavel</span>,
    "account_balance": <span className="material-icons">account_balance</span>,
    "people": <span className="material-icons">people</span>,
  };
  
  return departmentTemplates[deptId]?.icon ? 
    iconMap[departmentTemplates[deptId].icon] : 
    <BusinessIcon />;
};

export { departmentTemplates, getDepartmentIcon };